import { PARTICIPANT_SIGNER_TYPES, } from './create_consent_modal.types';
export const THIRD_PARTY_SIGNER_TYPE = 'third party';
export const getAssignableConsentResponseData = (body) => {
    if (Array.isArray(body)) {
        return body;
    }
    const data = body?.data;
    return Array.isArray(data) ? data : [];
};
const preferNonEmpty = (primary, fallback) => (primary?.length ? primary : fallback) ?? [];
const normalizeSignerType = (role) => role.ec__signer_type?.trim().toLowerCase() ?? '';
const getRuntimeFieldKey = (field) => {
    if (!field || typeof field !== 'object') {
        return undefined;
    }
    return field.ec__key?.trim() ?? '';
};
const hasDuplicate = (values) => new Set(values).size !== values.length;
export const getSignerRoleIdentity = (role) => role.ec__key?.trim() || role.ec__role?.trim() || '';
export const getTemplateValidationErrors = (template) => {
    const errors = [];
    const roles = template.ec__signer_roles ?? [];
    if (!template._id) {
        errors.push('template_id_missing');
    }
    if (!template.ec__title) {
        errors.push('template_title_missing');
    }
    if (roles.length === 0) {
        errors.push('signer_roles_missing');
    }
    if (roles.some((role) => !role.ec__role?.trim())) {
        errors.push('signer_role_label_missing');
    }
    if (roles.some((role) => !normalizeSignerType(role))) {
        errors.push('signer_type_missing');
    }
    const roleIdentities = roles.map(getSignerRoleIdentity).filter(Boolean);
    if (roleIdentities.length !== roles.length || hasDuplicate(roleIdentities)) {
        errors.push('signer_role_identity_invalid');
    }
    const runtimeKeys = [...(template.ec__requested_signatures ?? []), ...(template.ec__requested_data ?? [])]
        .map(getRuntimeFieldKey)
        .filter((key) => key !== undefined);
    if (runtimeKeys.length === 0) {
        errors.push('runtime_fields_missing');
    }
    if (runtimeKeys.some((key) => !key)) {
        errors.push('runtime_field_key_missing');
    }
    if (hasDuplicate(runtimeKeys)) {
        errors.push('runtime_field_key_duplicate');
    }
    return errors;
};
export const isAssignableTemplate = (template) => getTemplateValidationErrors(template).length === 0;
export const getAssignableConsentTemplates = (assignableConsents = []) => {
    return assignableConsents
        .filter(({ ec__assignable }) => ec__assignable !== false)
        .map(({ ec__document_template = {}, ec__published_studio_template }) => ({
        ...ec__document_template,
        _id: ec__document_template._id ?? '',
        ec__title: ec__document_template.ec__title ?? '',
        ec__published: ec__published_studio_template?.ec__published,
        ec__published_studio_template,
        ec__requested_data: preferNonEmpty(ec__document_template.ec__requested_data, ec__published_studio_template?.ec__requested_data),
        ec__requested_signatures: preferNonEmpty(ec__document_template.ec__requested_signatures, ec__published_studio_template?.ec__requested_signatures),
        ec__signer_roles: preferNonEmpty(ec__document_template.ec__signer_roles, ec__published_studio_template?.ec__signer_roles),
    }))
        .filter(isAssignableTemplate);
};
export const isParticipantRole = (role) => PARTICIPANT_SIGNER_TYPES.includes(normalizeSignerType(role));
export const isThirdPartyRole = (role) => normalizeSignerType(role) === THIRD_PARTY_SIGNER_TYPE;
export const getInternalSignerAccount = (signer) => {
    if (!signer) {
        return undefined;
    }
    const account = typeof signer.c_account === 'object' ? signer.c_account : undefined;
    return account ?? { email: signer.email, firstName: signer.firstName, lastName: signer.lastName };
};
export const getInternalSignerDisplayName = (signer) => {
    const account = getInternalSignerAccount(signer);
    const firstName = account?.firstName ?? account?.name?.first ?? '';
    const lastName = account?.lastName ?? account?.name?.last ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const email = account?.email ?? '';
    if (fullName && email) {
        return `${fullName} - ${email}`;
    }
    return fullName || email || signer._id;
};
export const getInternalSignerEmail = (signer) => {
    const account = getInternalSignerAccount(signer);
    return account?.email;
};
export const buildInvites = ({ template, participantId, participantEmail, internalSignersByRole = {}, thirdPartyEmailsByRole = {}, }) => {
    return (template.ec__signer_roles ?? [])
        .map((role) => {
        const roleIdentity = getSignerRoleIdentity(role);
        const invite = {
            ec__signer_role: role.ec__role,
            ec__signer_type: role.ec__signer_type ?? '',
            ec__required: role.ec__required,
            ec__order: role.ec__order,
            ec__key: role.ec__key,
        };
        if (isParticipantRole(role)) {
            if (participantEmail) {
                invite.ec__email = participantEmail;
            }
            else {
                invite.c_public_user = participantId;
            }
        }
        else if (isThirdPartyRole(role)) {
            const thirdPartyEmail = thirdPartyEmailsByRole[roleIdentity]?.trim();
            if (thirdPartyEmail) {
                invite.ec__email = thirdPartyEmail;
            }
        }
        else {
            const internalSignerEmail = getInternalSignerEmail(internalSignersByRole[roleIdentity]);
            if (internalSignerEmail) {
                invite.ec__email = internalSignerEmail;
            }
        }
        return invite;
    })
        .filter((invite) => !!invite.ec__email || !!invite.c_public_user);
};
export const participantHasSameEmailAsInternalSigner = (participantEmail, internalSigner) => {
    if (!participantEmail || !internalSigner) {
        return false;
    }
    const signerEmail = getInternalSignerEmail(internalSigner);
    return !!signerEmail && participantEmail === signerEmail;
};
export const participantHasSameEmailAsSignerEmail = (participantEmail, signerEmail) => !!participantEmail && !!signerEmail && participantEmail === signerEmail.trim();
export const buildInvitePayload = (args) => {
    const payload = {
        ec__document_template: args.template._id,
        c_site: args.siteId,
        invites: buildInvites(args),
        ec__pts: args.signatureType === 'pts',
    };
    if (args.template.ec__published_studio_template?._id) {
        payload.ec__published_studio_template = args.template.ec__published_studio_template._id;
    }
    return payload;
};
export const templateHasInternalSignerRole = (template) => {
    if (!template?.ec__signer_roles?.length) {
        return false;
    }
    return template.ec__signer_roles.some((role) => !isParticipantRole(role));
};
